// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 11-observability.md#per-app-metrics-persistence
// Resource-metrics panel for the app-detail Monitoring tab. Reads the latest
// 1-minute sample through the typed ts-rest client (`api.metrics.getLatest`),
// the REST mirror of the MCP getMetrics tool, and shows CPU/memory history
// below it (AppMetricsHistory). Renders CPU / memory / restarts with a distinct "no sample
// yet" state (collected_at null) and a per-metric "—" when a single value is
// missing (a metrics-server miss). request_rate / error_rate are collected but
// not surfaced here — charting them is a follow-up.
import * as React from 'react';

import { Activity } from 'lucide-react';

import { LiveIndicator } from '@/components/greenlight/LiveIndicator';
import { relativeTime, useTick } from '@/components/greenlight/relativeTime';
import { AD_CARD, AD_CARD_HD, AD_STAT } from '@/components/greenlight/surfaces';
import { bandColor } from '@/components/greenlight/MetricSparkline';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { metricsQueryOptions } from '@/lib/polling';
import { reservationGap, reservationUtilizationPercent, type AppMetrics } from '@greenlight/shared';

import { AppMetricsHistory } from './AppMetricsHistory';
import { AdEyebrow, AdStatTile } from './tab-primitives';

// A single value is missing only when it's null while a sample exists (e.g. the
// metrics-server didn't report CPU for that pod); render an em dash, not "0".
// Raw samples are unrounded floats, so trim to a sensible precision for display
// (CPU to 1 decimal, memory to whole MB).
function formatValue(value: number | null, suffix: string, digits: number): string {
  if (value === null) return '—';
  const rounded = digits === 0 ? Math.round(value) : Number(value.toFixed(digits));
  return `${rounded}${suffix}`;
}

// CPU tile value color follows the Meter/MetricSparkline threshold convention:
// green below warn (70%), amber at warn, red at crit (90%). Color is never the
// only signal — the numeric value carries the same information.
const CPU_THRESHOLDS = { warn: 70, crit: 90 } as const;

export function AppMetricsPanel({ appId }: { appId: string }): React.ReactElement {
  // staleTime matches the 1-minute sample resolution: switching away from the
  // Monitoring tab and back within a minute serves the cached snapshot without
  // a refetch (the tab content unmounts on switch), and a refetch sooner
  // couldn't return newer data anyway.
  const { data, isLoading, isError } = api.metrics.getLatest.useQuery(
    ['metrics', appId],
    { params: { id: appId } },
    { ...metricsQueryOptions, queryKey: ['metrics', appId] },
  );

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="metrics-loading">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  } else if (isError || !data || data.status !== 200) {
    body = (
      <Alert variant="destructive" data-testid="metrics-error">
        <AlertTitle>Couldn't load metrics</AlertTitle>
        <AlertDescription>Resource metrics are unavailable right now.</AlertDescription>
      </Alert>
    );
  } else {
    body = <MetricsContent metrics={data.body} appId={appId} />;
  }

  // Determine whether there's a live sample for the Live indicator.
  const hasLiveSample =
    !isLoading && !isError && data?.status === 200 && data.body.collected_at !== null;

  return (
    <div className={cn(AD_CARD, 'mb-9')} data-testid="metrics-panel">
      {/* Card header */}
      <div className={AD_CARD_HD}>
        <div className="flex items-center gap-2.5">
          <span className="grid h-7 w-7 place-items-center rounded-gl-icon bg-glow text-verdant">
            <Activity size={14} strokeWidth={1.6} aria-hidden />
          </span>
          <span className="text-[14px] font-semibold tracking-[-0.01em] text-foreground">
            Resource metrics
          </span>
        </div>
        {hasLiveSample && <LiveIndicator label="Live" />}
      </div>

      {/* Card body */}
      <div className="p-5 md:p-6">{body}</div>
    </div>
  );
}

function MetricsContent({
  metrics: m,
  appId,
}: {
  metrics: AppMetrics;
  appId: string;
}): React.ReactElement {
  // Re-render on a tick so "Collected X ago" stays current between data refetches.
  useTick(30_000);

  // No sample has been collected yet (a freshly registered or never-running app);
  // the whole record — collected_at included — is null. Show one explanatory line
  // rather than three em-dashed cards. With no current sample there's no history
  // either, so the charts are omitted in this state.
  if (m.collected_at === null) {
    return (
      <div
        className="rounded-gl-icon border border-border p-6 text-center text-sm text-muted-foreground"
        data-testid="metrics-empty"
      >
        No metrics collected yet. Samples appear at 1-minute resolution once the app is running.
      </div>
    );
  }

  // Any restart at all is worth a gentle amber signal; the count reinforces it
  // (color is never the only cue — the value carries the same information).
  const restartCount = m.restart_count ?? '—';
  const hasRestarts = typeof m.restart_count === 'number' && m.restart_count > 0;

  return (
    <div className="space-y-4">
      <div data-testid="metrics-grid">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {/* CPU — value text color follows the Meter/MetricSparkline threshold convention */}
          <CpuTile value={m.cpu_usage_percent} />
          {/* Memory — "MB" suffix rendered smaller per design spec; no threshold benchmark */}
          <MemoryTile value={m.memory_usage_mb} />
          {/* Restarts — amber tint when > 0 */}
          <AdStatTile
            label="Restarts"
            value={String(restartCount)}
            className={TILE_SURFACE[hasRestarts ? 'amber' : 'default']}
            valueClassName={TILE_VALUE_COLOR[hasRestarts ? 'amber' : 'default']}
          />
        </div>
        <ReservedVsUsed metrics={m} />
        {/* Freshness line — only when collected_at is non-null (enforced by parent guard) */}
        <p className="mt-2.5 text-[12px] text-muted-foreground">
          Collected {relativeTime(m.collected_at)}
        </p>
      </div>
      <AppMetricsHistory appId={appId} />
    </div>
  );
}

// Tone surface / value-color maps, kept local to the metric tiles so their
// tokens aren't coupled to a shared component at a different display size.
const TILE_SURFACE: Record<'default' | 'amber', string> = {
  default: '',
  amber: 'border-[hsl(var(--status-amber)/0.3)] bg-[hsl(var(--status-amber)/0.08)]',
};

const TILE_VALUE_COLOR: Record<'default' | 'amber', string> = {
  default: '',
  amber: 'text-[hsl(var(--status-amber-text))]',
};

interface ReservationRow {
  label: string;
  reserved: number;
  used: number | null;
  format: (value: number) => string;
}

const MILLICORES = (v: number): string => `${Math.round(v)}m`;
const MEGABYTES = (v: number): string => `${Math.round(v)} MB`;

function reservationRows(m: AppMetrics): ReservationRow[] {
  const rows: ReservationRow[] = [];
  if (m.cpu_reserved_m !== null) {
    rows.push({
      label: 'CPU',
      reserved: m.cpu_reserved_m,
      used: m.cpu_used_m,
      format: MILLICORES,
    });
  }
  if (m.memory_reserved_mb !== null) {
    rows.push({
      label: 'Memory',
      reserved: m.memory_reserved_mb,
      used: m.memory_usage_mb,
      format: MEGABYTES,
    });
  }
  return rows;
}

/**
 * What the scheduler holds for this app against what the app spends. Renders
 * nothing when there is no reservation to report (a sample written before the
 * field existed). A reservation with no usage beside it renders alone: an
 * unmeasured app is not an idle one.
 */
function ReservedVsUsed({ metrics: m }: { metrics: AppMetrics }): React.ReactElement | null {
  const rows = reservationRows(m);
  if (rows.length === 0) return null;

  return (
    <div className="mt-3 rounded-gl-icon border border-border p-4" data-testid="reserved-vs-used">
      <AdEyebrow>Reserved vs used</AdEyebrow>
      <dl className="mt-2 space-y-1.5">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-wrap items-baseline gap-x-2 text-[13px]">
            <dt className="text-muted-foreground">{row.label}</dt>
            <dd className="tabular-nums text-foreground">{reservationText(row)}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-2 text-[12px] text-muted-foreground">
        These are the app&apos;s requests, reserved for as long as it is deployed whether or not it
        spends them. Declaring <code>compute:</code> sets them to the limit; otherwise they are the
        baseline, which the app can burst above.
      </p>
    </div>
  );
}

/**
 * One row's sentence. Spending more than the reservation is the ordinary shape of
 * a baseline app — requests sit below the limit and the app bursts into the gap —
 * so it is stated as a fact rather than dressed as an overrun with a "0 unused"
 * and a percentage over 100, which reads as a prompt to reserve more.
 */
function reservationText(row: ReservationRow): string {
  if (row.used === null) return `${row.format(row.reserved)} reserved; usage not reported`;

  const used = `${row.format(row.used)} of ${row.format(row.reserved)} reserved`;
  const unused = reservationGap(row.reserved, row.used);
  const percent = reservationUtilizationPercent(row.reserved, row.used);
  if (unused === null || percent === null) return used;
  if (row.used > row.reserved) return `${used} (bursting above the reservation, up to the limit)`;
  if (unused === 0) return used;
  return `${used} (${row.format(unused)} unused, ${percent}% of the reservation in use)`;
}

// CPU tile applies threshold coloring to the value text — Meter/MetricSparkline
// convention with {warn:70, crit:90} (%). Color is never the only signal.
function CpuTile({ value }: { value: number | null }): React.ReactElement {
  const display = formatValue(value, '%', 1);
  // Derive color from the same bandColor function MetricSparkline uses so the
  // tile value and the history chart use the same green→amber→red ramp.
  const valueColor = value !== null ? bandColor(value, CPU_THRESHOLDS) : null;

  return (
    <div className={cn(AD_STAT)}>
      <AdEyebrow>CPU</AdEyebrow>
      <p
        className="mt-1.5 text-[30px] font-extrabold leading-none tracking-tight tabular-nums text-foreground"
        style={valueColor !== null ? { color: valueColor } : undefined}
        data-testid="cpu-value"
      >
        {display}
      </p>
    </div>
  );
}

// Memory tile renders the numeric value + a visually subordinate "MB" suffix.
// The suffix is intentionally smaller/muted while staying inside the same `<p>`
// element so screen readers and test queries get the full "142 MB" string.
function MemoryTile({ value }: { value: number | null }): React.ReactElement {
  const display = value === null ? '—' : String(Math.round(value));
  const showSuffix = value !== null;

  return (
    <div className={cn(AD_STAT)}>
      <AdEyebrow>Memory</AdEyebrow>
      <p className="mt-1.5 text-[30px] font-extrabold leading-none tracking-tight tabular-nums text-foreground">
        {display}
        {showSuffix && (
          <span className="ml-0.5 text-[15px] font-semibold text-muted-foreground"> MB</span>
        )}
      </p>
    </div>
  );
}
